<script setup lang="ts">
/**
 * Bilder des Projekts als Quelle für Bild-Slots. Zustand/Polling liegen in useDesignUploads
 * (bleibt aktiv, wenn das Panel geschlossen ist). Kacheln lassen sich auf die Bühne ziehen,
 * Dateien vom Rechner können aufs Panel (oder direkt auf die Bühne) fallen gelassen werden.
 */
import { computed, onBeforeUnmount, ref } from 'vue'
import type { UploadRef } from '@pfe/design-renderer'
import type { ImageRecord } from '../../../utils/imageDb'
import { isUploadReady, uploadRefOf, useDesignUploads } from '../../../design/useDesignUploads'
import { DESIGN_UPLOAD_MIME, setDraggedUpload } from '../../../design/dragTypes'
import { collectTags, hasTag, normalizeTags } from '../../../utils/tags'

const props = defineProps<{ targetLayerId?: string | null }>()
const emit = defineEmits<{ (e: 'pick', upload: UploadRef): void; (e: 'auto-fill', uploads: UploadRef[]): void }>()

const uploads = useDesignUploads()
const records = uploads.records
const busy = uploads.busy
const fileInput = ref<HTMLInputElement | null>(null)
const selectedIds = ref<string[]>([])
const dropActive = ref(false)
const objectUrls = new Map<string, string>()

function previewUrlFor(rec: ImageRecord): string {
  const u = rec.upload
  const remote = u?.thumbUrl || u?.previewUrl || u?.fullscreenPreviewUrl
  if (remote) return remote
  const cached = objectUrls.get(rec.id)
  if (cached) return cached
  const blob = rec.thumbBlob || rec.editorBlob || rec.workingBlob
  if (!blob) return ''
  const url = URL.createObjectURL(blob)
  objectUrls.set(rec.id, url)
  return url
}

const filterTag = ref('all')
const allTags = computed(() => collectTags(records.value as any))
const filteredRecords = computed(() => {
  if (filterTag.value === 'all') return records.value
  if (filterTag.value === 'untagged') return records.value.filter((r) => normalizeTags((r as any).tags).length === 0)
  return records.value.filter((r) => hasTag((r as any).tags, filterTag.value))
})
const items = computed(() => filteredRecords.value.map((rec) => ({ rec, url: previewUrlFor(rec), ready: isUploadReady(rec), state: rec.upload?.state || 'idle', pct: rec.upload?.progressPct || 0 })))

async function onFiles(list: FileList | File[] | null) {
  try {
    await uploads.importFiles(list)
  } finally {
    if (fileInput.value) fileInput.value.value = ''
  }
}

function toggle(id: string) {
  selectedIds.value = selectedIds.value.includes(id) ? selectedIds.value.filter((x) => x !== id) : [...selectedIds.value, id]
}
function pick(rec: ImageRecord) {
  const ref = uploadRefOf(rec)
  if (!ref) return
  emit('pick', ref)
}
function autoFill() {
  const chosen = (selectedIds.value.length ? records.value.filter((r) => selectedIds.value.includes(r.id)) : records.value).filter(isUploadReady).map(uploadRefOf).filter((x): x is UploadRef => !!x)
  emit('auto-fill', chosen)
}

// ---------- Drag & Drop ----------
function onTileDragStart(e: DragEvent, rec: ImageRecord, url: string) {
  const ref = uploadRefOf(rec)
  if (!ref || !e.dataTransfer) { e.preventDefault(); return }
  e.dataTransfer.effectAllowed = 'copy'
  e.dataTransfer.setData(DESIGN_UPLOAD_MIME, JSON.stringify(ref))
  e.dataTransfer.setData('text/plain', rec.name || ref.uploadId)
  setDraggedUpload(ref)
  const img = (e.currentTarget as HTMLElement).querySelector('img')
  if (img && url) e.dataTransfer.setDragImage(img, img.clientWidth / 2, img.clientHeight / 2)
}
function onTileDragEnd() {
  // Drop-Handler der Bühne liest synchron; danach aufräumen
  setTimeout(() => setDraggedUpload(null), 0)
}
function hasFiles(e: DragEvent) {
  return Array.from(e.dataTransfer?.types ?? []).includes('Files')
}
function onPanelDragOver(e: DragEvent) {
  if (!hasFiles(e)) return
  e.preventDefault()
  if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy'
  dropActive.value = true
}
function onPanelDragLeave(e: DragEvent) {
  const to = e.relatedTarget as Node | null
  if (!to || !(e.currentTarget as HTMLElement).contains(to)) dropActive.value = false
}
function onPanelDrop(e: DragEvent) {
  dropActive.value = false
  if (!hasFiles(e)) return
  e.preventDefault()
  void onFiles(Array.from(e.dataTransfer?.files ?? []))
}

onBeforeUnmount(() => {
  for (const u of objectUrls.values()) URL.revokeObjectURL(u)
  objectUrls.clear()
})
defineExpose({ reload: uploads.reload })
</script>

<template>
  <div class="ip" :class="{ 'ip--drop': dropActive }" @dragover="onPanelDragOver" @dragleave="onPanelDragLeave" @drop="onPanelDrop">
    <div class="ip__actions">
      <button type="button" class="ip__btn ip__btn--primary" :disabled="busy" @click="fileInput?.click()">{{ busy ? 'Wird importiert…' : 'Fotos hochladen' }}</button>
      <input ref="fileInput" type="file" accept="image/*,.heic,.heif,.tif,.tiff" multiple hidden @change="onFiles(($event.target as HTMLInputElement).files)" />
      <button type="button" class="ip__btn" :disabled="!items.some((i) => i.ready)" @click="autoFill">Automatisch füllen</button>
    </div>
    <label v-if="allTags.length" class="ip__filter">
      <span>Tag</span>
      <select v-model="filterTag">
        <option value="all">Alle ({{ records.length }})</option>
        <option value="untagged">Ohne Tag</option>
        <option v-for="t in allTags" :key="t.tag" :value="t.tag">{{ t.tag }} ({{ t.count }})</option>
      </select>
    </label>
    <p v-if="props.targetLayerId" class="ip__hint">Tippe ein Foto an, um es in den markierten Slot zu setzen.</p>
    <p v-else-if="items.length" class="ip__hint">Fotos antippen oder auf das Motiv ziehen.</p>
    <div v-if="!items.length" class="ip__empty">Noch keine Fotos in diesem Projekt. Fotos hierher oder direkt auf das Motiv ziehen.</div>
    <div class="ip__grid">
      <div v-for="it in items" :key="it.rec.id" class="ip__tile" :class="{ 'ip__tile--sel': selectedIds.includes(it.rec.id), 'ip__tile--pending': !it.ready }" :draggable="it.ready" @dragstart="onTileDragStart($event, it.rec, it.url)" @dragend="onTileDragEnd">
        <div class="ip__thumb" role="button" :tabindex="it.ready ? 0 : -1" :aria-disabled="!it.ready" :title="it.ready ? 'Einsetzen oder auf das Motiv ziehen' : 'Wird hochgeladen'" @click="it.ready && pick(it.rec)" @keydown.enter.prevent="it.ready && pick(it.rec)" @keydown.space.prevent="it.ready && pick(it.rec)">
          <img v-if="it.url" :src="it.url" :alt="it.rec.name" draggable="false" />
          <span v-if="!it.ready" class="ip__state">{{ it.state === 'failed' ? 'Fehler' : String(it.state) === 'processing' || it.pct >= 100 ? 'Verarbeitung…' : `${Math.round(it.pct)} %` }}</span>
        </div>
        <label class="ip__check"><input type="checkbox" :checked="selectedIds.includes(it.rec.id)" @change="toggle(it.rec.id)" /></label>
      </div>
    </div>
  </div>
</template>

<style scoped>
.ip { display: grid; gap: 10px; padding: 12px; min-height: 100%; align-content: start; border: 2px dashed transparent; border-radius: 10px; transition: border-color 0.12s, background 0.12s; }
.ip--drop { border-color: var(--pfe-color-accent, #e30b17); background: rgba(227, 11, 23, 0.04); }
.ip__actions { display: grid; gap: 6px; }
.ip__btn { padding: 9px 10px; border: 1px solid var(--pfe-color-border, rgba(15, 23, 42, 0.14)); border-radius: 8px; background: #fff; font: inherit; cursor: pointer; }
.ip__btn--primary { background: var(--pfe-color-accent, #e30b17); border-color: transparent; color: #fff; font-weight: 600; }
.ip__btn:disabled { opacity: 0.5; cursor: default; }
.ip__hint, .ip__empty { font-size: 12px; color: var(--pfe-color-muted, rgba(15, 23, 42, 0.62)); }
.ip__filter { display: flex; align-items: center; gap: 8px; font-size: 12px; }
.ip__filter select { flex: 1; min-width: 0; padding: 6px 8px; border: 1px solid var(--pfe-color-border, rgba(15, 23, 42, 0.14)); border-radius: 8px; font: inherit; font-size: 12px; background: #fff; }
.ip__grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(84px, 1fr)); gap: 8px; }
.ip__tile { position: relative; border-radius: 8px; overflow: hidden; border: 2px solid transparent; }
.ip__tile--sel { border-color: var(--pfe-color-accent, #e30b17); }
.ip__thumb { display: block; width: 100%; aspect-ratio: 1; padding: 0; border: 0; background: #f1f5f9; cursor: grab; position: relative; }
.ip__thumb[aria-disabled="true"] { cursor: default; }
.ip__thumb:focus-visible { outline: 2px solid var(--pfe-color-accent, #e30b17); outline-offset: -2px; }
.ip__thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
.ip__tile--pending .ip__thumb img { opacity: 0.5; }
.ip__state { position: absolute; inset: auto 0 0 0; padding: 2px; font-size: 11px; background: rgba(15, 23, 42, 0.6); color: #fff; }
.ip__check { position: absolute; top: 4px; left: 4px; }
</style>
